import { type TimeSlot } from "@src/appV2/Shifts/types";

export type TimeChargeDifferentialDayOfWeek =
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday";

export type QualificationRatesTableOverrides = Record<
  string,
  Partial<Record<TimeChargeDifferentialDayOfWeek, Partial<Record<TimeSlot, number>>>>
>;

export const DAYS_OF_WEEK = [
  { value: "sunday", label: "Sunday" },
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
] as const;

export type DayOfWeek = (typeof DAYS_OF_WEEK)[number]["value"];

export interface QualificationRateOverride {
  qualification: string;
  day: TimeChargeDifferentialDayOfWeek;
  slot: TimeSlot;
  rate: number;
}

export const DAY_ORDER: Record<DayOfWeek, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export const SLOT_ORDER: Record<TimeSlot, number> = {
  am: 0,
  pm: 1,
  noc: 2,
};

export function flattenOverrides(
  overrides: QualificationRatesTableOverrides | undefined
): QualificationRateOverride[] {
  if (!overrides) {
    return [];
  }

  const result: QualificationRateOverride[] = [];
  for (const [qualification, ratesTable] of Object.entries(overrides)) {
    if (!ratesTable) {
      continue;
    }
    for (const [day, slots] of Object.entries(ratesTable)) {
      if (!slots) {
        continue;
      }
      for (const [slot, rate] of Object.entries(slots)) {
        if (typeof rate === "number") {
          result.push({
            qualification,
            day: day as DayOfWeek,
            slot: slot as TimeSlot,
            rate,
          });
        }
      }
    }
  }

  result.sort((a, b) => {
    if (a.qualification !== b.qualification) {
      return a.qualification.localeCompare(b.qualification);
    }
    if (a.day !== b.day) {
      return DAY_ORDER[a.day] - DAY_ORDER[b.day];
    }
    return SLOT_ORDER[a.slot] - SLOT_ORDER[b.slot];
  });

  return result;
}

export function addOverride(
  overrides: QualificationRatesTableOverrides,
  override: QualificationRateOverride
): QualificationRatesTableOverrides {
  const { qualification, day, slot, rate } = override;
  const qualificationEntry = overrides[qualification] ?? {};
  const dayEntry = qualificationEntry[day] ?? {};
  return {
    ...overrides,
    [qualification]: {
      ...qualificationEntry,
      [day]: { ...dayEntry, [slot]: rate },
    },
  };
}

export function removeOverride(
  overrides: QualificationRatesTableOverrides,
  { qualification, day, slot }: QualificationRateOverride
): QualificationRatesTableOverrides {
  const qualificationEntry = overrides[qualification];
  if (!qualificationEntry) {
    return overrides;
  }

  const dayEntry = qualificationEntry[day];
  if (!dayEntry) {
    return overrides;
  }

  // Build new slots object without the removed slot
  const remainingSlots: Partial<Record<TimeSlot, number>> = {};
  for (const [slotKey, rate] of Object.entries(dayEntry)) {
    if (slotKey !== slot && typeof rate === "number") {
      remainingSlots[slotKey as TimeSlot] = rate;
    }
  }

  // If no slots remain, build without the day entry
  if (Object.keys(remainingSlots).length === 0) {
    const remainingDays: Partial<
      Record<TimeChargeDifferentialDayOfWeek, Partial<Record<TimeSlot, number>>>
    > = {};
    for (const [dayKey, slots] of Object.entries(qualificationEntry)) {
      if (dayKey !== day && slots) {
        remainingDays[dayKey as TimeChargeDifferentialDayOfWeek] = slots;
      }
    }

    // If no days remain, build without the qualification entry
    if (Object.keys(remainingDays).length === 0) {
      const remainingQualifications: QualificationRatesTableOverrides = {};
      for (const [qualKey, qualEntry] of Object.entries(overrides)) {
        if (qualKey !== qualification && qualEntry) {
          remainingQualifications[qualKey] = qualEntry;
        }
      }
      return remainingQualifications;
    }

    return {
      ...overrides,
      [qualification]: remainingDays,
    };
  }

  return {
    ...overrides,
    [qualification]: {
      ...qualificationEntry,
      [day]: remainingSlots,
    },
  };
}

export function hasOverride(
  overrides: QualificationRatesTableOverrides,
  qualification: string,
  day: TimeChargeDifferentialDayOfWeek,
  slot: TimeSlot
): boolean {
  return typeof overrides[qualification]?.[day]?.[slot] === "number";
}

export interface AddMultipleOverridesParams {
  overrides: QualificationRatesTableOverrides;
  qualifications: string[];
  days: DayOfWeek[];
  slots: TimeSlot[];
  rate: number;
}

export function addMultipleOverrides(
  params: AddMultipleOverridesParams
): QualificationRatesTableOverrides {
  const { overrides, qualifications, days, slots, rate } = params;
  let result = { ...overrides };
  for (const qualification of qualifications) {
    for (const day of days) {
      for (const slot of slots) {
        result = addOverride(result, { qualification, day, slot, rate });
      }
    }
  }
  return result;
}

export interface FindExistingOverridesParams {
  overrides: QualificationRatesTableOverrides;
  qualifications: string[];
  days: DayOfWeek[];
  slots: TimeSlot[];
}

function getOverrideIfExists(
  overrides: QualificationRatesTableOverrides,
  qualification: string,
  day: DayOfWeek,
  slot: TimeSlot
): QualificationRateOverride | undefined {
  const rate = overrides[qualification]?.[day]?.[slot];
  if (typeof rate === "number") {
    return { qualification, day, slot, rate };
  }
  return undefined;
}

export function findExistingOverrides(
  params: FindExistingOverridesParams
): QualificationRateOverride[] {
  const { overrides, qualifications, days, slots } = params;
  return qualifications.flatMap((qualification) =>
    days.flatMap((day) =>
      slots
        .map((slot) => getOverrideIfExists(overrides, qualification, day, slot))
        .filter((override): override is QualificationRateOverride => override !== undefined)
    )
  );
}
